import { acquireApiToken } from '../auth/msal';
import { GraphError } from '../graph/client';
import {
  isRetryableStatus,
  nextRetryDelay,
  parseRetryAfter,
  sleep,
} from '../graph/retry';
import { offlineAware } from './offline';

/**
 * Call the app's server API (same origin, so CSP connect-src 'self'
 * already covers it). The token rides in X-AuditPlus-Token rather than
 * Authorization (see api/README.md). Errors surface as GraphError with the
 * server's status/code so existing callers (404 → null, retry on 429/503)
 * behave the same on either path.
 */
export async function apiFetch<T>(
  path: string,
  options: {
    method?: 'GET' | 'POST';
    body?: unknown;
    /**
     * A WRITE: re-sent only when the server was throttled (429/503 — nothing
     * was done). A 502/504 may have done the work, so it isn't repeated.
     */
    write?: boolean;
  } = {},
): Promise<T> {
  const response = await apiRequest(path, options);
  return (await response.json()) as T;
}

/** At most this many file downloads at once (a record page can show 100+ photos — the per-user rate limit). */
const MAX_PARALLEL_DOWNLOADS = 4;
let downloading = 0;
const waiting: (() => void)[] = [];

/** File bytes from the server (downloads, Phase 6.5 step 3), a few at a time. */
export async function apiFetchBlob(path: string): Promise<Blob> {
  // A finished download hands its slot straight to the next one waiting.
  if (downloading >= MAX_PARALLEL_DOWNLOADS) await new Promise<void>((resolve) => waiting.push(resolve));
  else downloading += 1;
  try {
    return await (await apiRequest(path, {})).blob();
  } finally {
    const next = waiting.shift();
    if (next) next();
    else downloading -= 1;
  }
}

/** POST raw bytes (file uploads) and read the JSON answer. */
export async function apiUpload<T>(path: string, blob: Blob): Promise<T> {
  const response = await apiRequest(path, { method: 'POST', rawBody: blob, write: true });
  return (await response.json()) as T;
}

async function apiRequest(
  path: string,
  options: { method?: 'GET' | 'POST'; body?: unknown; rawBody?: Blob; write?: boolean },
): Promise<Response> {
  // Callers pass the path AFTER /api ("/reminders"); "/api/…" would ask for
  // /api/api/… — a 404 every time (UI review 2026-10-28: reminder history
  // never loaded). Refused here, whatever form the call takes.
  if (path.startsWith('/api/')) throw new Error(`API path "${path}" must not start with /api/ — the client adds it.`);
  let waited = 0;
  let giveUpWait: number | undefined;
  for (let attempt = 0; ; attempt++) {
    // No signal → OfflineError with a plain message (src/api/offline.ts).
    const response = await offlineAware(async () => fetch(`/api${path}`, {
      method: options.method ?? 'GET',
      headers: {
        'X-AuditPlus-Token': await acquireApiToken(),
        Accept: 'application/json',
        ...(options.rawBody !== undefined
          ? { 'Content-Type': options.rawBody.type || 'application/octet-stream' }
          : options.body !== undefined
            ? { 'Content-Type': 'application/json' }
            : {}),
      },
      ...(options.rawBody !== undefined
        ? { body: options.rawBody }
        : options.body !== undefined
          ? { body: JSON.stringify(options.body) }
          : {}),
    }), options.write === true);

    if (response.ok) return response;

    const retryable = options.write
      ? response.status === 429 || response.status === 503
      : isRetryableStatus(response.status);
    if (retryable) {
      const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
      const delay = nextRetryDelay(attempt, retryAfter, waited);
      if (delay !== null) {
        waited += delay;
        await sleep(delay);
        continue;
      }
      if (retryAfter !== undefined) giveUpWait = retryAfter * 1000;
    }

    let code = String(response.status);
    let message = response.statusText;
    try {
      const body = (await response.json()) as {
        error?: { code?: string; message?: string };
      };
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
    } catch {
      // Not JSON (e.g. the API isn't deployed yet); keep the status text.
    }
    throw new GraphError(response.status, code, message, giveUpWait);
  }
}
